import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import {
  Upload, Trash2, Star, StarOff, FileSpreadsheet, Loader2,
  CheckCircle, AlertCircle, ChevronDown, ChevronRight, Search,
  Plus, Play, Eye, Zap, X, AlertTriangle, ClipboardList, FileText, CheckCircle2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

const CARD_VARIANTS = {
  hidden:  { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120];

const EMPTY_JOB = { open: false, jobId: null, total: 0, done: 0, failed: 0, unverified: 0, dry_run: 0, status: 'running', rows: [] };

// Row outcome as the backend reports it: only `success` means D365 returned a record ID.
const ROW_OUTCOME = {
  success:    { label: 'Logged',     cls: 'text-[#22c55e]' },
  unverified: { label: 'Unverified', cls: 'text-[#f59e0b]' },
  dry_run:    { label: 'Dry run',    cls: 'text-[#9CA3AF]' },
  failed:     { label: 'Failed',     cls: 'text-[#ef4444]' },
};

function jobSummary(d) {
  const parts = [`${d.done} confirmed`];
  if (d.unverified) parts.push(`${d.unverified} unverified`);
  if (d.dry_run) parts.push(`${d.dry_run} dry run`);
  if (d.failed) parts.push(`${d.failed} failed`);
  return parts.join(', ');
}

function JobCounts({ job }) {
  return (
    <div className="flex justify-between text-xs text-[#9CA3AF] mb-1.5">
      <span>{job.done} of {job.total} confirmed</span>
      <span className="flex gap-3">
        {job.unverified > 0 && <span className="text-[#f59e0b]">{job.unverified} unverified</span>}
        {job.dry_run > 0 && <span className="text-[#9CA3AF]">{job.dry_run} dry run</span>}
        {job.failed > 0 && <span className="text-[#ef4444]">{job.failed} failed</span>}
      </span>
    </div>
  );
}

function JobRowOutcome({ row }) {
  const o = ROW_OUTCOME[row.status] || { label: row.status, cls: 'text-[#9CA3AF]' };
  return (
    <span className={`shrink-0 text-right ${o.cls}`} title={row.error_message || undefined}>
      {o.label}
      {row.error_code && <span className="ml-1.5 font-mono text-[10px] opacity-80">{row.error_code}</span>}
    </span>
  );
}

function AccountMatchCell({ match, onPick }) {
  if (!match) return <span className="text-[#9CA3AF]/50">—</span>;
  if (match.status === 'exact' || match.status === 'fuzzy' || match.status === 'mdm') {
    const label = match.status === 'fuzzy' ? `${Math.round(match.score * 100)}%`
      : match.status === 'mdm' ? 'by MDM ID' : 'exact';
    return (
      <span className="text-[#22c55e]">
        {match.account_name}
        <span className="ml-1.5 text-[10px] opacity-80">{label}</span>
        {match.mdm_id && <span className="block text-[10px] font-mono text-[#9CA3AF]">MDM {match.mdm_id}</span>}
      </span>
    );
  }
  if (match.status === 'ambiguous') {
    const options = match.candidate_options || [];
    if (onPick && options.length) {
      return (
        <select
          defaultValue=""
          onChange={(e) => { const o = options[Number(e.target.value)]; if (o) onPick(o); }}
          className="bg-[#0f0f10] border border-[#f59e0b]/40 text-[#f59e0b] text-xs rounded-none px-1.5 py-1 max-w-[220px]"
        >
          <option value="" disabled>Pick account ({options.length})</option>
          {options.map((o, i) => (
            <option key={`${o.mdm_id}-${i}`} value={i}>{o.account_name}{o.mdm_id ? ` · ${o.mdm_id}` : ''}</option>
          ))}
        </select>
      );
    }
    return (
      <span className="text-[#f59e0b]" title={(match.candidates || []).join(', ')}>
        Ambiguous ({(match.candidates || []).length})
      </span>
    );
  }
  return <span className="text-[#ef4444]">No match</span>;
}

const EMPTY_RULE_FORM = {
  name: '',
  subject_template: 'Activity with {name}',
  duration_minutes: 30,
  notes_template: '',
};

export default function FileManagementPage() {
  // ── File state ──────────────────────────────────────────────────────────────
  const [files, setFiles]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [uploading, setUploading]       = useState(false);
  const [dragOver, setDragOver]         = useState(false);
  const [expandedFileId, setExpandedFileId] = useState(null);
  const [accountsState, setAccountsState]   = useState({});
  const fileInputRef  = useRef(null);
  const searchTimers  = useRef({});

  // ── Rules state ─────────────────────────────────────────────────────────────
  const [rules, setRules]               = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [showCreateRule, setShowCreateRule] = useState(false);
  const [ruleForm, setRuleForm]         = useState(EMPTY_RULE_FORM);
  const [creatingRule, setCreatingRule] = useState(false);

  // Delete confirmations
  const [deleteFileTarget, setDeleteFileTarget] = useState(null); // { fileId, filename }
  const [deleteRuleTarget, setDeleteRuleTarget] = useState(null); // { ruleId, ruleName }

  // Preview modal
  const [preview, setPreview] = useState({ open: false, rule: null, rows: [], loading: false });

  // Rules job progress modal
  const [job, setJob] = useState(EMPTY_JOB);
  const jobPollRef = useRef(null);

  // ── Activity Sheet state ─────────────────────────────────────────────────────
  const [sheetTab, setSheetTab]             = useState('paste'); // 'paste' | 'upload'
  const [sheetText, setSheetText]           = useState('');
  const [sheetFile, setSheetFile]           = useState(null);
  const [sheetParsing, setSheetParsing]     = useState(false);
  const [sheetParseResult, setSheetParseResult] = useState(null); // { new_rows, duplicate_rows, unverified_rows, total_new, total_duplicate, total_unverified }

  // Ambiguous row → user picks the account; its MDM ID travels with the row and the backend trusts it.
  const pickSheetAccount = (serialNo, option) => {
    const apply = (rows) => (rows || []).map((r) => (r.serial_no !== serialNo ? r : {
      ...r,
      mdm_id: option.mdm_id,
      account_match: { status: 'exact', account_name: option.account_name, mdm_id: option.mdm_id, score: 1 },
    }));
    setSheetParseResult((prev) => prev && {
      ...prev, new_rows: apply(prev.new_rows), unverified_rows: apply(prev.unverified_rows),
    });
  };
  const [sheetDragOver, setSheetDragOver]   = useState(false);
  const sheetFileInputRef = useRef(null);

  // Sheet job progress modal
  const [sheetJob, setSheetJob] = useState(EMPTY_JOB);
  const [sheetResend, setSheetResend] = useState(false); // also send rows the flow accepted but never confirmed
  const sheetJobPollRef = useRef(null);

  // ── Load files ───────────────────────────────────────────────────────────────
  const loadFiles = () => {
    setLoading(true);
    api.get('/files/')
      .then((res) => setFiles(Array.isArray(res?.data?.files) ? res.data.files : []))
      .catch((err) => toast.error('Failed to load files', { description: err?.message }))
      .finally(() => setLoading(false));
  };

  // ── Load rules ───────────────────────────────────────────────────────────────
  const loadRules = useCallback(() => {
    setRulesLoading(true);
    api.get('/excel/rules')
      .then((res) => setRules(res?.data?.rules || []))
      .catch(() => toast.error('Failed to load rules'))
      .finally(() => setRulesLoading(false));
  }, []);

  useEffect(() => { loadFiles(); loadRules(); }, [loadRules]);

  // ── Upload (account file) ────────────────────────────────────────────────────
  const uploadFile = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      toast.error('Unsupported file type', { description: 'Only .xlsx or .csv files supported.' });
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/files/upload-accounts`, {
        method: 'POST', credentials: 'include', body: formData,
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || json.detail || 'Upload failed');
      toast.success('File uploaded', {
        description: `${json.data.imported_accounts} accounts imported from ${json.data.filename}.`,
      });
      loadFiles();
    } catch (err) {
      toast.error('Upload failed', { description: err.message });
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) uploadFile(f); };
  const handleFileInput = (e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ''; };

  // ── Set default ──────────────────────────────────────────────────────────────
  const setDefault = async (fileId) => {
    try {
      await api.post(`/files/${fileId}/set-default`);
      setFiles((prev) => prev.map((f) => ({ ...f, is_default: f.file_id === fileId })));
      toast.success('Default file updated');
    } catch { toast.error('Failed to update default'); }
  };

  // ── Delete file ──────────────────────────────────────────────────────────────
  const deleteFile = (fileId, filename) => setDeleteFileTarget({ fileId, filename });

  const confirmDeleteFile = async () => {
    if (!deleteFileTarget) return;
    const { fileId } = deleteFileTarget;
    setDeleteFileTarget(null);
    try {
      await api.delete(`/files/${fileId}`);
      setFiles((prev) => prev.filter((f) => f.file_id !== fileId));
      if (expandedFileId === fileId) setExpandedFileId(null);
      toast.success('File deleted');
    } catch { toast.error('Failed to delete file'); }
  };

  // ── Expand / account preview ─────────────────────────────────────────────────
  const fetchAccounts = useCallback(async (fileId, q = '') => {
    setAccountsState((prev) => ({ ...prev, [fileId]: { ...prev[fileId], loading: true, q } }));
    try {
      const res = await api.get(`/files/${fileId}/accounts?q=${encodeURIComponent(q)}&limit=100`);
      setAccountsState((prev) => ({ ...prev, [fileId]: { loading: false, accounts: res?.data?.accounts || [], q } }));
    } catch {
      setAccountsState((prev) => ({ ...prev, [fileId]: { loading: false, accounts: [], q } }));
    }
  }, []);

  const toggleExpand = async (fileId) => {
    if (expandedFileId === fileId) { setExpandedFileId(null); return; }
    setExpandedFileId(fileId);
    if (!accountsState[fileId]) await fetchAccounts(fileId, '');
  };

  const handleAccountSearch = (fileId, q) => {
    setAccountsState((prev) => ({ ...prev, [fileId]: { ...prev[fileId], q } }));
    clearTimeout(searchTimers.current[fileId]);
    searchTimers.current[fileId] = setTimeout(() => fetchAccounts(fileId, q), 300);
  };

  // ── Create rule ───────────────────────────────────────────────────────────────
  const handleCreateRule = async (e) => {
    e.preventDefault();
    if (!ruleForm.name.trim())             { toast.error('Rule name is required'); return; }
    if (!ruleForm.subject_template.trim()) { toast.error('Subject template is required'); return; }
    setCreatingRule(true);
    try {
      const res = await api.post('/excel/rules', {
        name: ruleForm.name.trim(),
        subject_template: ruleForm.subject_template.trim(),
        duration_minutes: Number(ruleForm.duration_minutes),
        notes_template: ruleForm.notes_template.trim(),
        account_filter: 'all',
      });
      setRules((prev) => [res.data.rule, ...prev]);
      setRuleForm(EMPTY_RULE_FORM);
      setShowCreateRule(false);
      toast.success('Rule created');
    } catch (err) {
      toast.error('Failed to create rule', { description: err?.response?.data?.detail || err.message });
    } finally {
      setCreatingRule(false);
    }
  };

  // ── Delete rule ───────────────────────────────────────────────────────────────
  const handleDeleteRule = (ruleId, ruleName) => setDeleteRuleTarget({ ruleId, ruleName });

  const confirmDeleteRule = async () => {
    if (!deleteRuleTarget) return;
    const { ruleId } = deleteRuleTarget;
    setDeleteRuleTarget(null);
    try {
      await api.delete(`/excel/rules/${ruleId}`);
      setRules((prev) => prev.filter((r) => r.id !== ruleId));
      toast.success('Rule deleted');
    } catch { toast.error('Failed to delete rule'); }
  };

  // ── Preview rule ──────────────────────────────────────────────────────────────
  const handlePreview = async (rule) => {
    setPreview({ open: true, rule, rows: [], loading: true });
    try {
      const res = await api.post(`/excel/rules/${rule.id}/preview`);
      setPreview((prev) => ({ ...prev, rows: res?.data?.rows || [], loading: false }));
    } catch (err) {
      setPreview((prev) => ({ ...prev, loading: false }));
      toast.error('Preview failed', { description: err?.response?.data?.detail || err.message });
    }
  };

  // ── Run rule ──────────────────────────────────────────────────────────────────
  const startRun = async (rule) => {
    setPreview({ open: false, rule: null, rows: [], loading: false });
    try {
      const res = await api.post(`/excel/rules/${rule.id}/execute`, { account_ids: null });
      const { job_id, total } = res.data;
      setJob({ ...EMPTY_JOB, open: true, jobId: job_id, total });
      clearInterval(jobPollRef.current);
      jobPollRef.current = setInterval(() => pollJob(job_id), 2000);
    } catch (err) {
      toast.error('Failed to start run', { description: err?.response?.data?.detail || err.message });
    }
  };

  const pollJob = async (jobId) => {
    try {
      const res = await api.get(`/excel/jobs/${jobId}`);
      const d = res?.data;
      setJob((prev) => ({ ...prev, total: d.total, done: d.done, failed: d.failed, unverified: d.unverified || 0, dry_run: d.dry_run || 0, status: d.status, rows: Array.isArray(d.rows) ? d.rows : [] }));
      if (d.status === 'complete') {
        clearInterval(jobPollRef.current);
        jobPollRef.current = null;
        const notify = d.failed || d.unverified ? toast.warning : toast.success;
        notify(`Bulk run complete — ${jobSummary(d)}`);
      }
    } catch { /* silent retry */ }
  };

  const closeJob = () => {
    clearInterval(jobPollRef.current);
    jobPollRef.current = null;
    setJob(EMPTY_JOB);
  };

  // ── Activity Sheet handlers ───────────────────────────────────────────────────
  const handleSheetFileDrop = (e) => {
    e.preventDefault();
    setSheetDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setSheetFile(f);
  };

  const handleSheetFileInput = (e) => {
    const f = e.target.files?.[0];
    if (f) setSheetFile(f);
    e.target.value = '';
  };

  const handleSheetParse = async () => {
    setSheetParsing(true);
    setSheetParseResult(null);
    setSheetResend(false);
    try {
      let res;
      if (sheetTab === 'paste') {
        res = await api.post('/activity-sheets/parse-text', { text: sheetText });
      } else {
        if (!sheetFile) { toast.error('No file selected'); setSheetParsing(false); return; }
        const formData = new FormData();
        formData.append('file', sheetFile);
        const fetchRes = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/activity-sheets/parse-file`, {
          method: 'POST', credentials: 'include', body: formData,
        });
        const json = await fetchRes.json();
        if (!fetchRes.ok) throw new Error(json.error || json.detail || 'Parse failed');
        res = json;
      }
      setSheetParseResult(res.data);
    } catch (err) {
      toast.error('Parse failed', { description: err?.response?.data?.detail || err.message });
    } finally {
      setSheetParsing(false);
    }
  };

  const handleSheetExecute = async () => {
    const rows = [
      ...(sheetParseResult?.new_rows || []),
      ...(sheetResend ? sheetParseResult?.unverified_rows || [] : []),
    ];
    if (!rows.length) return;
    try {
      const res = await api.post('/activity-sheets/execute', { rows });
      const { job_id, total } = res.data;
      setSheetJob({ ...EMPTY_JOB, open: true, jobId: job_id, total });
      clearInterval(sheetJobPollRef.current);
      sheetJobPollRef.current = setInterval(() => pollSheetJob(job_id), 2000);
    } catch (err) {
      toast.error('Failed to start sheet log', { description: err?.response?.data?.detail || err.message });
    }
  };

  const pollSheetJob = async (jobId) => {
    try {
      const res = await api.get(`/activity-sheets/jobs/${jobId}`);
      const d = res?.data?.data || res?.data;
      setSheetJob((prev) => ({ ...prev, total: d.total, done: d.done, failed: d.failed, unverified: d.unverified || 0, dry_run: d.dry_run || 0, status: d.status, rows: Array.isArray(d.rows) ? d.rows : [] }));
      if (d.status === 'complete') {
        clearInterval(sheetJobPollRef.current);
        sheetJobPollRef.current = null;
        const notify = d.failed || d.unverified ? toast.warning : toast.success;
        notify(`Sheet log complete — ${jobSummary(d)}`);
      }
    } catch { /* silent retry */ }
  };

  const closeSheetJob = () => {
    clearInterval(sheetJobPollRef.current);
    sheetJobPollRef.current = null;
    setSheetJob(EMPTY_JOB);
  };

  // Cleanup both poll refs on unmount
  useEffect(() => () => {
    clearInterval(jobPollRef.current);
    clearInterval(sheetJobPollRef.current);
  }, []);

  // Duplicate MDM detection for preview warning
  const previewDuplicates = (() => {
    const counts = {};
    for (const row of preview.rows) {
      const id = row.mdm_id || row.account_id;
      if (id) counts[id] = (counts[id] || 0) + 1;
    }
    return Object.entries(counts).filter(([, c]) => c > 1).map(([id]) => id);
  })();

  // ── RENDER ───────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-5xl mx-auto">

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <FileSpreadsheet className="w-5 h-5 text-[#FF4500]" />
          <h1 className="font-['Space_Grotesk'] text-2xl font-bold text-[#F2F3F5]">File Management</h1>
        </div>
        <p className="text-[#9CA3AF] text-sm">
          Upload Excel or CSV account lists. Set one as default to power account search and automation rules.
        </p>
      </div>

      {/* Upload area */}
      <motion.div variants={CARD_VARIANTS} initial="hidden" animate="visible">
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={`relative bg-[#141416] border-2 border-dashed p-10 flex flex-col items-center gap-3 cursor-pointer transition-colors ${
            dragOver ? 'border-[#FF4500] bg-[#FF4500]/5' : 'border-[#1f2022] hover:border-[#FF4500]/40'
          } ${uploading ? 'pointer-events-none' : ''}`}
        >
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFileInput} />
          {uploading ? <Loader2 className="w-8 h-8 text-[#FF4500] animate-spin" /> : <Upload className="w-8 h-8 text-[#9CA3AF]" />}
          <div className="text-center">
            <p className="text-[#F2F3F5] text-sm font-medium">{uploading ? 'Uploading…' : 'Drop your file here or click to browse'}</p>
            <p className="text-[#9CA3AF] text-xs mt-1">.xlsx, .xls, .csv — your account list</p>
          </div>
        </div>
      </motion.div>

      {/* Column detection note */}
      <div className="mt-3 mb-6 flex items-start gap-2 text-xs text-[#9CA3AF]">
        <CheckCircle className="w-3.5 h-3.5 text-[#22c55e] shrink-0 mt-0.5" />
        <span>
          Columns auto-detected: <strong className="text-[#F2F3F5]">IDG</strong> → MDM ID (IDG),{' '}
          <strong className="text-[#F2F3F5]">ISG</strong> → MDM ID (ISG),{' '}
          <strong className="text-[#F2F3F5]">party_name / account name / customer name</strong> → account name.
        </span>
      </div>

      {/* Files table */}
      <div>
        <h2 className="font-['Space_Grotesk'] text-base font-semibold text-[#F2F3F5] mb-3">Uploaded Files</h2>
        {loading ? (
          <div className="bg-[#141416] border border-[#1f2022] p-8 flex justify-center">
            <Loader2 className="w-5 h-5 text-[#9CA3AF] animate-spin" />
          </div>
        ) : files.length === 0 ? (
          <div className="bg-[#141416] border border-[#1f2022] p-10 flex flex-col items-center gap-2 text-[#9CA3AF]">
            <FileSpreadsheet className="w-8 h-8 opacity-40" />
            <p className="text-sm">No files uploaded yet</p>
          </div>
        ) : (
          <div className="bg-[#141416] border border-[#1f2022] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1f2022]">
                  <th className="px-4 py-3 text-left text-xs text-[#9CA3AF] font-medium uppercase tracking-wider w-8"></th>
                  <th className="px-4 py-3 text-left text-xs text-[#9CA3AF] font-medium uppercase tracking-wider">Filename</th>
                  <th className="px-4 py-3 text-left text-xs text-[#9CA3AF] font-medium uppercase tracking-wider">Accounts</th>
                  <th className="px-4 py-3 text-left text-xs text-[#9CA3AF] font-medium uppercase tracking-wider hidden md:table-cell">Uploaded</th>
                  <th className="px-4 py-3 text-left text-xs text-[#9CA3AF] font-medium uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs text-[#9CA3AF] font-medium uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {files.map((f) => (
                  <Fragment key={f.file_id}>
                    <tr
                      className="border-b border-[#1f2022] hover:bg-[#1f2022]/30 cursor-pointer transition-colors"
                      onClick={() => toggleExpand(f.file_id)}
                    >
                      <td className="px-4 py-3 text-[#9CA3AF]">
                        {expandedFileId === f.file_id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4 text-[#9CA3AF] shrink-0" />
                          <span className="text-[#F2F3F5] text-sm truncate max-w-[200px]">{f.filename}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#9CA3AF] text-sm">{f.row_count?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-[#9CA3AF] text-xs hidden md:table-cell">
                        {f.uploaded_at ? new Date(f.uploaded_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {f.is_default
                          ? <Badge className="bg-[#FF4500]/10 text-[#FF4500] border-[#FF4500]/20 rounded-none text-[10px] px-2 py-0.5 hover:bg-[#FF4500]/10">Default</Badge>
                          : <span className="text-[#9CA3AF] text-xs">—</span>
                        }
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {!f.is_default && (
                            <button onClick={() => setDefault(f.file_id)} title="Set as default"
                              className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] hover:text-[#FF4500] transition-colors">
                              <StarOff className="w-4 h-4" />
                            </button>
                          )}
                          {f.is_default && (
                            <button disabled title="Default file"
                              className="w-7 h-7 flex items-center justify-center text-[#FF4500] opacity-60 cursor-default">
                              <Star className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={() => deleteFile(f.file_id, f.filename)} title="Delete file"
                            className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] hover:text-[#ef4444] transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>

                    <AnimatePresence>
                      {expandedFileId === f.file_id && (
                        <tr className="border-b border-[#1f2022]">
                          <td colSpan={6} className="px-4 pb-4 pt-0">
                            <motion.div
                              initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="bg-[#0f0f10] border border-[#1f2022] mt-2">
                                <div className="flex items-center gap-2 px-3 py-2 border-b border-[#1f2022]">
                                  <Search className="w-3.5 h-3.5 text-[#9CA3AF] shrink-0" />
                                  <input
                                    type="text" placeholder="Search accounts in this file…"
                                    value={accountsState[f.file_id]?.q || ''}
                                    onChange={(e) => handleAccountSearch(f.file_id, e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="bg-transparent text-[#F2F3F5] text-xs outline-none flex-1 placeholder:text-[#9CA3AF]"
                                  />
                                  {accountsState[f.file_id]?.loading && <Loader2 className="w-3.5 h-3.5 text-[#9CA3AF] animate-spin shrink-0" />}
                                </div>
                                <div className="max-h-60 overflow-y-auto">
                                  {accountsState[f.file_id]?.loading && !accountsState[f.file_id]?.accounts?.length ? (
                                    <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-[#9CA3AF]" /></div>
                                  ) : accountsState[f.file_id]?.accounts?.length === 0 ? (
                                    <p className="text-center text-xs text-[#9CA3AF] py-6">No accounts found</p>
                                  ) : (
                                    <table className="w-full text-xs">
                                      <thead className="sticky top-0 bg-[#0f0f10]">
                                        <tr className="border-b border-[#1f2022]">
                                          <th className="px-3 py-2 text-left text-[#9CA3AF] font-medium">Account Name</th>
                                          <th className="px-3 py-2 text-left text-[#9CA3AF] font-medium">MDM ID (IDG)</th>
                                          <th className="px-3 py-2 text-left text-[#9CA3AF] font-medium hidden sm:table-cell">MDM ID (ISG)</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {accountsState[f.file_id].accounts.map((a, i) => (
                                          <tr key={i} className="border-b border-[#1f2022]/40 last:border-0">
                                            <td className="px-3 py-1.5 text-[#F2F3F5]">{a.account_name || '—'}</td>
                                            <td className="px-3 py-1.5 text-[#9CA3AF] font-mono">{a.l2_mdm_id_idg || '—'}</td>
                                            <td className="px-3 py-1.5 text-[#9CA3AF] font-mono hidden sm:table-cell">{a.l2_mdm_id_isg || '—'}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                                <div className="px-3 py-2 border-t border-[#1f2022] text-[10px] text-[#9CA3AF]">
                                  Showing {accountsState[f.file_id]?.accounts?.length || 0} of {f.row_count?.toLocaleString()} accounts
                                  {accountsState[f.file_id]?.q && ' matching search'}
                                </div>
                              </div>
                            </motion.div>
                          </td>
                        </tr>
                      )}
                    </AnimatePresence>
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─────────────────── Automation Rules ─────────────────────────────────── */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#FF4500]" />
            <h2 className="font-['Space_Grotesk'] text-base font-semibold text-[#F2F3F5]">Automation Rules</h2>
          </div>
          <button
            onClick={() => setShowCreateRule((v) => !v)}
            className="flex items-center gap-1.5 h-8 px-3 bg-[#FF4500] hover:bg-[#FF4500]/80 text-white text-xs font-medium transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            {showCreateRule ? 'Cancel' : 'Create Rule'}
          </button>
        </div>
        <p className="text-[#9CA3AF] text-xs mb-4">
          Define rules to bulk-log D365 activities for every account in your default file. Each rule runs against the live default account list.
        </p>

        {/* Create rule form */}
        <AnimatePresence>
          {showCreateRule && (
            <motion.div
              initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }}
              className="overflow-hidden mb-4"
            >
              <form onSubmit={handleCreateRule} className="bg-[#141416] border border-[#FF4500]/20 p-5 space-y-4">
                <p className="text-xs font-medium text-[#FF4500] uppercase tracking-wider">New Rule</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[#9CA3AF] mb-1">Rule Name</label>
                    <input type="text" value={ruleForm.name}
                      onChange={(e) => setRuleForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="e.g. Weekly Q3 check-in"
                      className="w-full h-9 px-3 bg-[#0f0f10] border border-[#1f2022] text-[#F2F3F5] text-sm placeholder-[#9CA3AF] focus:outline-none focus:border-[#FF4500] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#9CA3AF] mb-1">Activity Type</label>
                    <div className="h-9 px-3 bg-[#0f0f10] border border-[#1f2022] flex items-center">
                      <span className="text-[#F2F3F5] text-sm">Appointment · Customer Meeting</span>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs text-[#9CA3AF] mb-1">
                      Subject Template
                      <span className="ml-1 text-[#9CA3AF]/60">— use <code className="text-[#FF4500]">{'{name}'}</code> for account name</span>
                    </label>
                    <input type="text" value={ruleForm.subject_template}
                      onChange={(e) => setRuleForm((p) => ({ ...p, subject_template: e.target.value }))}
                      placeholder="e.g. Q3 check-in with {name}"
                      className="w-full h-9 px-3 bg-[#0f0f10] border border-[#1f2022] text-[#F2F3F5] text-sm placeholder-[#9CA3AF] focus:outline-none focus:border-[#FF4500] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#9CA3AF] mb-1">Duration (minutes)</label>
                    <select value={ruleForm.duration_minutes}
                      onChange={(e) => setRuleForm((p) => ({ ...p, duration_minutes: Number(e.target.value) }))}
                      className="w-full h-9 px-3 bg-[#0f0f10] border border-[#1f2022] text-[#F2F3F5] text-sm focus:outline-none focus:border-[#FF4500] transition-colors appearance-none"
                    >
                      {DURATION_OPTIONS.map((d) => <option key={d} value={d}>{d} min</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#9CA3AF] mb-1">Notes Template <span className="text-[#9CA3AF]/60">(optional)</span></label>
                    <input type="text" value={ruleForm.notes_template}
                      onChange={(e) => setRuleForm((p) => ({ ...p, notes_template: e.target.value }))}
                      placeholder="e.g. Automated log for {name}"
                      className="w-full h-9 px-3 bg-[#0f0f10] border border-[#1f2022] text-[#F2F3F5] text-sm placeholder-[#9CA3AF] focus:outline-none focus:border-[#FF4500] transition-colors"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button type="submit" disabled={creatingRule}
                    className="h-9 px-5 bg-[#FF4500] hover:bg-[#FF4500]/80 text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2">
                    {creatingRule && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Create Rule
                  </button>
                  <button type="button" onClick={() => { setShowCreateRule(false); setRuleForm(EMPTY_RULE_FORM); }}
                    className="h-9 px-5 bg-[#1f2022] hover:bg-[#1f2022]/70 text-[#9CA3AF] text-sm transition-colors">
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Rules list */}
        {rulesLoading ? (
          <div className="bg-[#141416] border border-[#1f2022] p-8 flex justify-center">
            <Loader2 className="w-5 h-5 text-[#9CA3AF] animate-spin" />
          </div>
        ) : rules.length === 0 ? (
          <div className="bg-[#141416] border border-[#1f2022] p-10 flex flex-col items-center gap-2 text-[#9CA3AF]">
            <Zap className="w-8 h-8 opacity-30" />
            <p className="text-sm">No automation rules yet</p>
            <p className="text-xs opacity-60">Create a rule to bulk-log activities for all accounts in your default file</p>
          </div>
        ) : (
          <div className="space-y-2">
            {rules.map((rule) => (
              <div key={rule.id} className="bg-[#141416] border border-[#1f2022] px-4 py-3 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-[#F2F3F5] truncate">{rule.name}</span>
                    <Badge className="rounded-none text-[10px] px-1.5 py-0 border hover:bg-inherit bg-blue-500/10 text-blue-400 border-blue-500/20">
                      Appointment
                    </Badge>
                  </div>
                  <p className="text-xs text-[#9CA3AF] mt-0.5 truncate">
                    "{rule.subject_template}" · {rule.duration_minutes} min
                    {rule.notes_template ? ` · ${rule.notes_template}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => handlePreview(rule)} title="Preview accounts"
                    className="flex items-center gap-1 h-7 px-2.5 text-xs text-[#9CA3AF] hover:text-[#F2F3F5] border border-[#1f2022] hover:border-[#9CA3AF] transition-colors">
                    <Eye className="w-3.5 h-3.5" />Preview
                  </button>
                  <button onClick={() => startRun(rule)} title="Run rule"
                    className="flex items-center gap-1 h-7 px-2.5 text-xs text-white bg-[#FF4500] hover:bg-[#FF4500]/80 transition-colors">
                    <Play className="w-3.5 h-3.5" />Run
                  </button>
                  <button onClick={() => handleDeleteRule(rule.id, rule.name)} title="Delete rule"
                    className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] hover:text-[#ef4444] transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─────────────────── Activity Sheet Log ───────────────────────────────── */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-2">
          <ClipboardList className="w-4 h-4 text-[#FF4500]" />
          <h2 className="font-['Space_Grotesk'] text-base font-semibold text-[#F2F3F5]">Activity Sheet Log</h2>
        </div>
        <p className="text-[#9CA3AF] text-xs mb-4">
          Paste or upload a table of meeting records to bulk-log them to D365 as appointments. Duplicate Serial Nos are skipped automatically.
        </p>

        <div className="bg-[#141416] border border-[#1f2022] p-5 space-y-4">
          {/* Tabs */}
          <div className="flex border-b border-[#1f2022]">
            <button
              onClick={() => { setSheetTab('paste'); setSheetParseResult(null); }}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium transition-colors border-b-2 -mb-px ${
                sheetTab === 'paste'
                  ? 'border-[#FF4500] text-[#F2F3F5]'
                  : 'border-transparent text-[#9CA3AF] hover:text-[#F2F3F5]'
              }`}
            >
              <FileText className="w-3.5 h-3.5" />
              Paste Data
            </button>
            <button
              onClick={() => { setSheetTab('upload'); setSheetParseResult(null); }}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium transition-colors border-b-2 -mb-px ${
                sheetTab === 'upload'
                  ? 'border-[#FF4500] text-[#F2F3F5]'
                  : 'border-transparent text-[#9CA3AF] hover:text-[#F2F3F5]'
              }`}
            >
              <Upload className="w-3.5 h-3.5" />
              Upload File
            </button>
          </div>

          {/* Tab content */}
          {sheetTab === 'paste' ? (
            <textarea
              value={sheetText}
              onChange={(e) => { setSheetText(e.target.value); setSheetParseResult(null); }}
              rows={6}
              placeholder={`Paste your Excel data here (Ctrl+C from Excel, or markdown table)...\nSerial No  Date        Time      Regarding              Notes\n1          01-05-2026  11:00 AM  Neuland                Discussed endpoints and Lenovo portfolio`}
              className="w-full px-3 py-2 bg-[#0f0f10] border border-[#1f2022] text-[#F2F3F5] text-xs font-mono placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#FF4500] transition-colors resize-none"
            />
          ) : (
            <div>
              <div
                onDragOver={(e) => { e.preventDefault(); setSheetDragOver(true); }}
                onDragLeave={() => setSheetDragOver(false)}
                onDrop={handleSheetFileDrop}
                onClick={() => sheetFileInputRef.current?.click()}
                className={`relative bg-[#0f0f10] border-2 border-dashed p-6 flex flex-col items-center gap-2 cursor-pointer transition-colors ${
                  sheetDragOver ? 'border-[#FF4500] bg-[#FF4500]/5' : 'border-[#1f2022] hover:border-[#FF4500]/40'
                }`}
              >
                <input ref={sheetFileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleSheetFileInput} />
                <Upload className="w-6 h-6 text-[#9CA3AF]" />
                <div className="text-center">
                  {sheetFile ? (
                    <p className="text-[#F2F3F5] text-xs font-medium">{sheetFile.name}</p>
                  ) : (
                    <>
                      <p className="text-[#F2F3F5] text-xs font-medium">Drop your activity sheet here or click to browse</p>
                      <p className="text-[#9CA3AF] text-[10px] mt-0.5">.xlsx, .xls, .csv</p>
                    </>
                  )}
                </div>
                {sheetFile && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setSheetFile(null); setSheetParseResult(null); }}
                    className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center text-[#9CA3AF] hover:text-[#ef4444] transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Parse button */}
          <button
            onClick={handleSheetParse}
            disabled={sheetParsing || (sheetTab === 'paste' ? !sheetText.trim() : !sheetFile)}
            className="flex items-center gap-2 h-9 px-5 bg-[#1f2022] hover:bg-[#2a2b2d] text-[#F2F3F5] text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {sheetParsing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            {sheetParsing ? 'Parsing…' : 'Parse & Preview'}
          </button>

          {/* Parse result preview */}
          {sheetParseResult && (
            <div className="space-y-3">
              {/* Counts */}
              <div className="flex items-center gap-3 text-xs">
                <span className="text-[#9CA3AF]">
                  <span className="text-[#F2F3F5] font-medium">{sheetParseResult.total_new ?? sheetParseResult.new_rows?.length ?? 0}</span> new records
                  {' · '}
                  <span className="text-[#9CA3AF]">{sheetParseResult.total_duplicate ?? sheetParseResult.duplicate_rows?.length ?? 0} already logged</span>
                  {(sheetParseResult.total_unverified ?? 0) > 0 && (
                    <>
                      {' · '}
                      <span className="text-[#f59e0b]">{sheetParseResult.total_unverified} unverified</span>
                    </>
                  )}
                </span>
              </div>

              {/* Rows the flow accepted earlier but never confirmed */}
              {(sheetParseResult.total_unverified ?? 0) > 0 && (
                <label className="flex items-start gap-2 p-3 bg-[#f59e0b]/5 border border-[#f59e0b]/20 text-xs text-[#f59e0b] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sheetResend}
                    onChange={(e) => setSheetResend(e.target.checked)}
                    className="mt-0.5 accent-[#FF4500]"
                  />
                  <span>
                    Resend {sheetParseResult.total_unverified} unverified {sheetParseResult.total_unverified === 1 ? 'row' : 'rows'}.
                    D365 never confirmed these — check D365 first, resending can create a duplicate.
                  </span>
                </label>
              )}

              {/* All-duplicate info box */}
              {(sheetParseResult.total_new ?? sheetParseResult.new_rows?.length ?? 0) === 0 &&
                (sheetParseResult.total_unverified ?? 0) === 0 && (
                <div className="flex items-start gap-2 p-3 bg-blue-500/5 border border-blue-500/20 text-xs text-blue-400">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>All serial numbers have already been logged.</span>
                </div>
              )}

              {/* Preview table */}
              {(() => {
                const allRows = [
                  ...(sheetParseResult.new_rows || []).map((r) => ({ ...r, _status: 'new' })),
                  ...(sheetParseResult.unverified_rows || []).map((r) => ({ ...r, _status: 'unverified' })),
                  ...(sheetParseResult.duplicate_rows || []).map((r) => ({ ...r, _status: 'duplicate' })),
                ].sort((a, b) => (Number(a.serial_no) || 0) - (Number(b.serial_no) || 0));
                if (allRows.length === 0) return null;
                return (
                  <div className="border border-[#1f2022] overflow-hidden">
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-[#141416]">
                          <tr className="border-b border-[#1f2022]">
                            <th className="px-3 py-2 text-left text-[#9CA3AF] font-medium w-16">Serial No</th>
                            <th className="px-3 py-2 text-left text-[#9CA3AF] font-medium w-24">Date</th>
                            <th className="px-3 py-2 text-left text-[#9CA3AF] font-medium w-20">Time</th>
                            <th className="px-3 py-2 text-left text-[#9CA3AF] font-medium">Regarding</th>
                            <th className="px-3 py-2 text-left text-[#9CA3AF] font-medium">Account match</th>
                            <th className="px-3 py-2 text-left text-[#9CA3AF] font-medium hidden md:table-cell">Notes</th>
                            <th className="px-3 py-2 text-left text-[#9CA3AF] font-medium w-28">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allRows.map((row, i) => (
                            <tr key={i} className="border-b border-[#1f2022]/40 last:border-0">
                              <td className="px-3 py-1.5 text-[#9CA3AF] font-mono">{row.serial_no ?? '—'}</td>
                              <td className="px-3 py-1.5 text-[#F2F3F5]">{row.date ?? '—'}</td>
                              <td className="px-3 py-1.5 text-[#9CA3AF]">{row.time ?? '—'}</td>
                              <td className="px-3 py-1.5 text-[#F2F3F5]">{row.regarding ?? '—'}</td>
                              <td className="px-3 py-1.5"><AccountMatchCell match={row.account_match} onPick={(o) => pickSheetAccount(row.serial_no, o)} /></td>
                              <td className="px-3 py-1.5 text-[#9CA3AF] hidden md:table-cell">
                                {row.notes ? (row.notes.length > 60 ? `${row.notes.slice(0, 60)}…` : row.notes) : '—'}
                              </td>
                              <td className="px-3 py-1.5">
                                {row._status === 'new' ? (
                                  <span className="bg-green-500/10 text-green-400 border border-green-500/20 rounded-none text-[10px] px-1.5 py-0.5 inline-block">New</span>
                                ) : row._status === 'unverified' ? (
                                  <span className="bg-[#f59e0b]/10 text-[#f59e0b] border border-[#f59e0b]/20 rounded-none text-[10px] px-1.5 py-0.5 inline-block">Unverified</span>
                                ) : (
                                  <span className="bg-[#1f2022] text-[#9CA3AF] border border-[#1f2022] rounded-none text-[10px] px-1.5 py-0.5 inline-block">Already Logged</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Execute button */}
              {(() => {
                const sendCount = (sheetParseResult.total_new ?? sheetParseResult.new_rows?.length ?? 0)
                  + (sheetResend ? sheetParseResult.total_unverified ?? 0 : 0);
                return (
                  <button
                    onClick={handleSheetExecute}
                    disabled={sendCount === 0}
                    className="flex items-center gap-2 h-9 px-5 bg-[#FF4500] hover:bg-[#FF4500]/80 text-white text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Play className="w-3.5 h-3.5" />
                    Log {sendCount} Records to D365
                  </button>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* How it works */}
      <div className="mt-8 bg-[#141416] border border-[#1f2022] p-5">
        <div className="flex items-center gap-2 mb-3">
          <AlertCircle className="w-4 h-4 text-[#9CA3AF]" />
          <p className="text-xs text-[#9CA3AF] font-medium uppercase tracking-wider">How it works</p>
        </div>
        <ul className="space-y-1.5 text-xs text-[#9CA3AF]">
          <li>• Upload your account list — the file is parsed and stored in the database.</li>
          <li>• Set one file as <strong className="text-[#F2F3F5]">Default</strong> — it powers account search in activity forms and automation rules.</li>
          <li>• Click any file row to expand and preview or search the accounts inside it.</li>
          <li>• <strong className="text-[#F2F3F5]">Automation Rules</strong> — create a rule, preview which accounts will be logged, then hit Run to bulk-log to D365 in the background.</li>
          <li>• MDM IDs starting with <strong className="text-[#FF4500]">PA0</strong> are highlighted in previews. Duplicate MDM IDs trigger a conflict warning before running.</li>
          <li>• <strong className="text-[#F2F3F5]">Activity Sheet Log</strong> — paste or upload a meeting table, preview new vs already-logged rows, then log new records to D365.</li>
        </ul>
      </div>

      {/* ── Preview Modal ────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {preview.open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setPreview((p) => ({ ...p, open: false }))}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }} transition={{ duration: 0.15 }}
              className="bg-[#141416] border border-[#1f2022] w-full max-w-2xl max-h-[80vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f2022]">
                <div>
                  <p className="font-['Space_Grotesk'] text-sm font-semibold text-[#F2F3F5]">Preview: {preview.rule?.name}</p>
                  <p className="text-xs text-[#9CA3AF] mt-0.5">
                    {preview.loading ? 'Loading…' : `${preview.rows.length} accounts will be logged`}
                  </p>
                </div>
                <button onClick={() => setPreview((p) => ({ ...p, open: false }))}
                  className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] hover:text-[#F2F3F5] transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {previewDuplicates.length > 0 && (
                <div className="mx-5 mt-3 flex items-start gap-2 p-3 bg-yellow-500/5 border border-yellow-500/20 text-xs text-yellow-400">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>{previewDuplicates.length} duplicate MDM {previewDuplicates.length === 1 ? 'ID' : 'IDs'} detected — these accounts will each be logged individually.</span>
                </div>
              )}

              <div className="flex-1 overflow-y-auto">
                {preview.loading ? (
                  <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#9CA3AF]" /></div>
                ) : preview.rows.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-12 text-[#9CA3AF]">
                    <AlertCircle className="w-6 h-6 opacity-40" />
                    <p className="text-sm">No accounts in default file</p>
                    <p className="text-xs opacity-60">Upload a file and set it as default first</p>
                  </div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[#141416]">
                      <tr className="border-b border-[#1f2022]">
                        <th className="px-5 py-2.5 text-left text-[#9CA3AF] font-medium">Account</th>
                        <th className="px-5 py-2.5 text-left text-[#9CA3AF] font-medium">MDM ID</th>
                        <th className="px-5 py-2.5 text-left text-[#9CA3AF] font-medium">Subject</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((row, i) => {
                        const mdmId = row.mdm_id || row.account_id || '';
                        const isPA  = mdmId.toUpperCase().startsWith('PA0');
                        const isDup = previewDuplicates.includes(mdmId);
                        return (
                          <tr key={i} className={`border-b border-[#1f2022]/40 last:border-0 ${isDup ? 'bg-yellow-500/5' : ''}`}>
                            <td className="px-5 py-2 text-[#F2F3F5]">{row.name || '—'}</td>
                            <td className="px-5 py-2 font-mono">
                              {mdmId ? <span className={isPA ? 'text-[#FF4500]' : 'text-[#9CA3AF]'}>{mdmId}</span>
                                     : <span className="text-[#9CA3AF]/50">—</span>}
                            </td>
                            <td className="px-5 py-2 text-[#9CA3AF]">{row.subject || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="flex items-center justify-between px-5 py-3 border-t border-[#1f2022]">
                <span className="text-xs text-[#9CA3AF]">{preview.rows.length} accounts · Appointment</span>
                <div className="flex gap-2">
                  <button onClick={() => setPreview((p) => ({ ...p, open: false }))}
                    className="h-8 px-4 text-xs text-[#9CA3AF] border border-[#1f2022] hover:border-[#9CA3AF] transition-colors">
                    Close
                  </button>
                  {preview.rows.length > 0 && (
                    <button onClick={() => startRun(preview.rule)}
                      className="h-8 px-4 text-xs text-white bg-[#FF4500] hover:bg-[#FF4500]/80 flex items-center gap-1.5 transition-colors">
                      <Play className="w-3 h-3" />Run Now ({preview.rows.length})
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Rules Job Progress Modal ─────────────────────────────────────────── */}
      <AnimatePresence>
        {job.open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }} transition={{ duration: 0.2 }}
              className="bg-[#141416] border border-[#1f2022] w-full max-w-xl"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f2022]">
                <div className="flex items-center gap-2">
                  {job.status === 'running'
                    ? <Loader2 className="w-4 h-4 text-[#FF4500] animate-spin" />
                    : <CheckCircle className="w-4 h-4 text-[#22c55e]" />}
                  <p className="font-['Space_Grotesk'] text-sm font-semibold text-[#F2F3F5]">
                    {job.status === 'running' ? 'Logging Activities…' : 'Bulk Run Complete'}
                  </p>
                </div>
                {job.status === 'complete' && (
                  <button onClick={closeJob}
                    className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] hover:text-[#F2F3F5] transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="px-5 py-4 space-y-4">
                <div>
                  <JobCounts job={job} />
                  <div className="h-1.5 bg-[#1f2022] overflow-hidden">
                    <motion.div className="h-full bg-[#FF4500]"
                      animate={{ width: `${job.total ? ((job.done + job.failed + job.unverified + job.dry_run) / job.total) * 100 : 0}%` }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                </div>

                {job.rows.filter((r) => r.status !== 'pending').length > 0 && (
                  <div className="max-h-48 overflow-y-auto space-y-0.5">
                    {job.rows.filter((r) => r.status !== 'pending').slice(-20).map((row, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-[#1f2022]/30 last:border-0">
                        <span className="text-[#9CA3AF] truncate flex-1">{row.name || row.account_id || '—'}</span>
                        <span className="ml-2"><JobRowOutcome row={row} /></span>
                      </div>
                    ))}
                  </div>
                )}

                {job.status === 'complete' && (
                  <button onClick={closeJob}
                    className="h-8 px-4 text-xs text-white bg-[#FF4500] hover:bg-[#FF4500]/80 transition-colors">
                    Done
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Delete File Confirmation ─────────────────────────────────────────── */}
      <AlertDialog open={!!deleteFileTarget} onOpenChange={(o) => !o && setDeleteFileTarget(null)}>
        <AlertDialogContent className="bg-[#141416] border border-[#1f2022] text-[#F2F3F5] rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-['Space_Grotesk'] text-[#F2F3F5]">Delete file?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9CA3AF]">
              <strong className="text-[#F2F3F5]">{deleteFileTarget?.filename}</strong> and all its imported accounts will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#1f2022] text-[#9CA3AF] hover:bg-[#1f2022] rounded-none">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteFile} className="bg-[#ef4444] hover:bg-[#dc2626] text-white rounded-none border-0">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Delete Rule Confirmation ──────────────────────────────────────────── */}
      <AlertDialog open={!!deleteRuleTarget} onOpenChange={(o) => !o && setDeleteRuleTarget(null)}>
        <AlertDialogContent className="bg-[#141416] border border-[#1f2022] text-[#F2F3F5] rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-['Space_Grotesk'] text-[#F2F3F5]">Delete rule?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9CA3AF]">
              <strong className="text-[#F2F3F5]">{deleteRuleTarget?.ruleName}</strong> will be permanently removed and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#1f2022] text-[#9CA3AF] hover:bg-[#1f2022] rounded-none">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteRule} className="bg-[#ef4444] hover:bg-[#dc2626] text-white rounded-none border-0">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Sheet Job Progress Modal ─────────────────────────────────────────── */}
      <AnimatePresence>
        {sheetJob.open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }} transition={{ duration: 0.2 }}
              className="bg-[#141416] border border-[#1f2022] w-full max-w-xl"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f2022]">
                <div className="flex items-center gap-2">
                  {sheetJob.status === 'running'
                    ? <Loader2 className="w-4 h-4 text-[#FF4500] animate-spin" />
                    : <CheckCircle2 className="w-4 h-4 text-[#22c55e]" />}
                  <p className="font-['Space_Grotesk'] text-sm font-semibold text-[#F2F3F5]">
                    {sheetJob.status === 'running' ? 'Logging Sheet Records…' : 'Sheet Log Complete'}
                  </p>
                </div>
                {sheetJob.status === 'complete' && (
                  <button onClick={closeSheetJob}
                    className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] hover:text-[#F2F3F5] transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="px-5 py-4 space-y-4">
                <div>
                  <JobCounts job={sheetJob} />
                  <div className="h-1.5 bg-[#1f2022] overflow-hidden">
                    <motion.div className="h-full bg-[#FF4500]"
                      animate={{ width: `${sheetJob.total ? ((sheetJob.done + sheetJob.failed + sheetJob.unverified + sheetJob.dry_run) / sheetJob.total) * 100 : 0}%` }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                </div>

                {sheetJob.rows.filter((r) => r.status !== 'pending').length > 0 && (
                  <div className="max-h-48 overflow-y-auto space-y-0.5">
                    {sheetJob.rows.filter((r) => r.status !== 'pending').slice(-20).map((row, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-[#1f2022]/30 last:border-0">
                        <span className="text-[#9CA3AF] font-mono shrink-0 w-12">{row.serial_no ?? '—'}</span>
                        <span className="text-[#9CA3AF] truncate flex-1 mx-2">{row.regarding || '—'}</span>
                        <JobRowOutcome row={row} />
                      </div>
                    ))}
                  </div>
                )}

                {sheetJob.status === 'complete' && (
                  <button onClick={closeSheetJob}
                    className="h-8 px-4 text-xs text-white bg-[#FF4500] hover:bg-[#FF4500]/80 transition-colors">
                    Done
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
